import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
import { DealerInfoEntities, DealerInfoModel, getDealerInfoPath } from 'client/data/models/dealer-info';
import { InventoryEntities } from 'client/data/models/inventory';

import {
  getDealerWebsiteUrl,
  getDealerWebsiteTrackingParams,
  getDealerWebsitesValue,
  updateDealerWebsiteTrackingParams,
  addAdditionalUTMParams,
} from 'site-modules/shared/utils/dealership-link-constructor';
import { generateDeepLink } from 'site-modules/shared/utils/car-buying-online/generate-deep-link';
import { hasDealerAddressInfo } from 'site-modules/shared/utils/inventory-utils/has-dealer-address-info';
import { isCarMax, isCarZingPartner, isDriveway, isPrivateParty } from 'site-modules/shared/utils/dealer-details-utils';
import { DealerInfoContent } from 'client/site-modules/shared/components/inventory/dealer-info-content/dealer-info-content';

/**
 * Shows section with dealer information (name, phone, direction and dealer srp links).
 */
export function DealerInfoUI({
  dealerInfo = {},
  vinData = {},
  pageNameLegacy = '',
  zipCode,
  ted6504Recipe = 'ctrl',
  isForceDefault = false,
}) {
  if (!hasDealerAddressInfo(vinData)) {
    return null;
  }
  const parentDealershipName = vinData?.dealerInfo?.displayInfo?.parentDealershipName ?? '';

  const deepLinkUrl = vinData?.deepLinkUrl ?? getDealerWebsitesValue(dealerInfo);
  const dealerWebSiteUrl = getDealerWebsiteUrl(deepLinkUrl);
  const dealerWebSiteTrackingParams = getDealerWebsiteTrackingParams(deepLinkUrl);
  const genericDeepLinkUrl = getDealerWebsitesValue(dealerInfo);
  const genericDeepLink = generateDeepLink(vinData) || genericDeepLinkUrl;

  const genericUrl = `${getDealerWebsiteUrl(genericDeepLink)}${updateDealerWebsiteTrackingParams({
    deepLinkUrl: genericDeepLinkUrl,
    dealer: parentDealershipName,
    zipCode,
    isDealerInfo: true,
    ted6504Recipe,
    isForceDefault,
  })}`;
  const trackingParams = isDriveway(parentDealershipName)
    ? updateDealerWebsiteTrackingParams({
        deepLinkUrl: dealerWebSiteTrackingParams,
        dealer: parentDealershipName,
        zipCode,
        isDealerInfo: true,
        type: vinData?.type,
      })
    : dealerWebSiteTrackingParams;
  const dealerLink = isCarMax(parentDealershipName)
    ? addAdditionalUTMParams({ deepLink: genericUrl, dealer: parentDealershipName })
    : `${dealerWebSiteUrl}${trackingParams}`;
  const isCarZingPartnerDealer = isCarZingPartner(vinData?.dealerInfo?.partnerCode);
  const carZingDealerUrl = isCarZingPartnerDealer && vinData?.listingUrl;

  return (
    <DealerInfoContent
      vinData={vinData}
      pageNameLegacy={pageNameLegacy}
      dealerWebSiteUrl={carZingDealerUrl || dealerWebSiteUrl}
      url={carZingDealerUrl || dealerLink}
      isCarZingPartner={isCarZingPartnerDealer}
    />
  );
}

DealerInfoUI.propTypes = {
  dealerInfo: DealerInfoEntities.DealerInfo,
  vinData: InventoryEntities.InventoryVin,
  pageNameLegacy: PropTypes.string,
  zipCode: PropTypes.string.isRequired,
  ted6504Recipe: PropTypes.string,
  isForceDefault: PropTypes.bool,
};

const mapStateToProps = state => ({
  pageNameLegacy: state?.pageContext?.legacy?.pageName ?? state?.pageContext?.page?.name,
  zipCode: state.visitor.location.zipCode,
  isForceDefault: state?.forceDefault,
});

const stateToPropsConfig = {
  dealerInfo: bindToPath(
    ({ vinData, isSrp }) => (isSrp || isPrivateParty(vinData) ? null : getDealerInfoPath(vinData)),
    DealerInfoModel
  ),
};

export const DealerInfo = connect(mapStateToProps)(connectToModel(DealerInfoUI, stateToPropsConfig));
